import type { Ref } from 'vue'
import jobAssignmentFullCommitSvg from '~/assets/images/job/job_assignment_full_commit.svg?url'
import jobAssignmentProjectAssignSvg from '~/assets/images/job/job_assignment_project_assign.svg?url'

export const OCCUPATION_OTHER_ID = 30
export const REQUIRED_SKILL_IS_REQUIRED_MAX = 3
export const TEAM_MEMBERS_MAX = 10
export const JOB_LOCATION_MAX = 5

export const organizationAffiliationTypes = {
  DESIGN_CREATIVE: 1,
  PRODUCT: 2,
  DEVELOPMENT: 3,
  MARKETING: 4,
  CORPORATE: 5,
  OTHER: 6,
}

export const organizationAffiliationTypeItems = [
  {
    value: organizationAffiliationTypes.DESIGN_CREATIVE,
    label: 'デザイン組織',
  },
  { value: organizationAffiliationTypes.PRODUCT, label: 'プロダクト組織' },
  { value: organizationAffiliationTypes.DEVELOPMENT, label: '開発組織' },
  {
    value: organizationAffiliationTypes.MARKETING,
    label: 'マーケティング組織',
  },
  { value: organizationAffiliationTypes.CORPORATE, label: 'コーポレート組織' },
  { value: organizationAffiliationTypes.OTHER, label: 'その他' },
]

export const assignmentTypeTypes = {
  FULL_COMMIT: 1,
  PROJECT_ASSIGN: 2,
  OTHER: 3,
}

export const assignmentTypeTypeItems = [
  {
    value: assignmentTypeTypes.FULL_COMMIT,
    label: '専任・フルコミット',
    description:
      '特定のプロダクトやプロジェクトの専任として、全てのリソースを注ぐ',
    image: jobAssignmentFullCommitSvg,
  },
  {
    value: assignmentTypeTypes.PROJECT_ASSIGN,
    label: '案件・プロジェクトアサイン',
    description:
      '案件やプロジェクトごとに都度アサインされ、柔軟にリソースを分配する',
    image: jobAssignmentProjectAssignSvg,
  },
  { value: assignmentTypeTypes.OTHER, label: 'その他' },
]

export const jobV2LocationTypes = {
  OFFICE: 1,
  OFFICE_AND_REMOTE: 2,
  FULL_REMOTE: 3,
} as const
export type JobV2LocationType =
  (typeof jobV2LocationTypes)[keyof typeof jobV2LocationTypes]

export const jobV2LocationTypeItems = [
  { value: jobV2LocationTypes.OFFICE, label: '出社' },
  {
    value: jobV2LocationTypes.OFFICE_AND_REMOTE,
    label: 'リモート・出社',
  },
  {
    value: jobV2LocationTypes.FULL_REMOTE,
    label: 'フルリモート',
  },
]

export const officeAttendanceFrequencyTypes = {
  WEEK_4_OR_MORE: 1,
  WEEK_3: 2,
  WEEK_2: 3,
  WEEK_1: 4,
  LESS_THAN_WEEK_1: 5,
  OTHER: 6,
}

export const officeAttendanceFrequencyItems = [
  {
    value: officeAttendanceFrequencyTypes.WEEK_4_OR_MORE,
    label: '週4出社以上',
  },
  { value: officeAttendanceFrequencyTypes.WEEK_3, label: '週3出社' },
  { value: officeAttendanceFrequencyTypes.WEEK_2, label: '週2出社' },
  { value: officeAttendanceFrequencyTypes.WEEK_1, label: '週1出社' },
  { value: officeAttendanceFrequencyTypes.LESS_THAN_WEEK_1, label: '週1未満' },
  { value: officeAttendanceFrequencyTypes.OTHER, label: 'その他' },
]

export const passiveSmokingPreventionTypes = {
  NO_SMOKING_ON_PREMISES_NO_OUTDOOR: 1,
  NO_SMOKING_ON_PREMISES_WITH_OUTDOOR: 2,
  NO_SMOKING_INDOORS: 3,
  SMOKING_ROOM_AVAILABLE: 4,
  SMOKING_AREA_WORK: 5,
  NO_PREVENTION: 6,
} as const

export type PassiveSmokingMeasureType =
  (typeof passiveSmokingPreventionTypes)[keyof typeof passiveSmokingPreventionTypes]

export const passiveSmokingPreventionItems = [
  {
    value: passiveSmokingPreventionTypes.NO_SMOKING_ON_PREMISES_NO_OUTDOOR,
    label: '敷地内禁煙（屋外喫煙可の場所なし）',
  },
  {
    value: passiveSmokingPreventionTypes.NO_SMOKING_ON_PREMISES_WITH_OUTDOOR,
    label: '敷地内禁煙（屋外に喫煙場所あり）',
  },
  {
    value: passiveSmokingPreventionTypes.NO_SMOKING_INDOORS,
    label: '屋内禁煙（屋外喫煙可）',
  },
  {
    value: passiveSmokingPreventionTypes.SMOKING_ROOM_AVAILABLE,
    label: '屋内原則禁煙（喫煙専用室設置あり）',
  },
  {
    value: passiveSmokingPreventionTypes.SMOKING_AREA_WORK,
    label: '喫煙可能区域での業務あり',
  },
  {
    value: passiveSmokingPreventionTypes.NO_PREVENTION,
    label: '対策なし',
  },
]

export const contractPeriodTypes = {
  NO_PERIOD: 1,
  HAS_PERIOD: 2,
}

export const contractPeriodTypeItems = [
  { value: contractPeriodTypes.NO_PERIOD, label: '期間の定めなし' },
  { value: contractPeriodTypes.HAS_PERIOD, label: '期間の定めあり' },
]

export const contractRenewalTypes = {
  NO_RENEWAL: 1,
  HAS_RENEWAL: 2,
}

export const contractRenewalTypeItems = [
  { value: contractRenewalTypes.NO_RENEWAL, label: '更新なし' },
  { value: contractRenewalTypes.HAS_RENEWAL, label: '更新あり' },
]

export const contractRenewalUpperLimitTypes = {
  NO_UPPER_LIMIT: 1,
  HAS_UPPER_LIMIT: 2,
}

export const contractRenewalUpperLimitTypeItems = [
  { value: contractRenewalUpperLimitTypes.NO_UPPER_LIMIT, label: '上限なし' },
  { value: contractRenewalUpperLimitTypes.HAS_UPPER_LIMIT, label: '上限あり' },
]

export const employmentTypes = {
  FULL_TIME_EMPLOYEE: 1, // 正社員
  CONTRACT_EMPLOYEE: 2, // 契約社員
  CONTRACTOR: 3, // 業務委託
}

export const employmentTypeItems = [
  { value: employmentTypes.FULL_TIME_EMPLOYEE, label: '正社員' },
  { value: employmentTypes.CONTRACTOR, label: '業務委託' },
  { value: employmentTypes.CONTRACT_EMPLOYEE, label: '契約社員' },
]

export const jobV2PublicFlg = {
  PROVISIONAL: 0,
  DRAFT: 1,
  RECRUITING: 2,
  STOP: 4,
} as const
export type JobV2PublicFlg =
  (typeof jobV2PublicFlg)[keyof typeof jobV2PublicFlg]

export const jobV2RequestStatus = {
  NONE: 0,
  UNDER_REVIEW_PUBLISH: 11,
  NEED_TO_FIX_PUBLISH: 12,
  UNDER_REVIEW_PUBLISH_AFTER_FIX: 13,
  UNDER_REVIEW_UPDATE: 21,
  NEED_TO_FIX_UPDATE: 22,
  UNDER_REVIEW_UPDATE_AFTER_FIX: 23,
} as const
export type JobV2RequestStatus =
  (typeof jobV2RequestStatus)[keyof typeof jobV2RequestStatus]

export const jobV2ModifyRequestIssueRequestStatus = {
  NONE: 0,
  DRAFT: 1,
  REQUESTED: 2,
  APPROVED: 3,
} as const
export type JobV2ModifyRequestIssueRequestStatus =
  (typeof jobV2ModifyRequestIssueRequestStatus)[keyof typeof jobV2ModifyRequestIssueRequestStatus]

export const jobV2ModifyRequestIssueModifyStatus = {
  NONE: 0,
  UNCHECKED: 1,
  CHECKED: 2,
} as const
export type JobV2ModifyRequestIssueModifyStatus =
  (typeof jobV2ModifyRequestIssueModifyStatus)[keyof typeof jobV2ModifyRequestIssueModifyStatus]

export const jobV2ModifyRequestIssueCheckStatus = {
  NONE: 0,
  UNCHECKED: 1,
  CHECKED: 2,
  APPROVED: 3,
} as const
export type JobV2ModifyRequestIssueCheckStatus =
  (typeof jobV2ModifyRequestIssueCheckStatus)[keyof typeof jobV2ModifyRequestIssueCheckStatus]

export type JobV2ModifyRequestIssueTarget =
  | 'jobTitle'
  | 'displayOccupation'
  | 'coverImage'
  | 'employment'
  | 'salary'
  | 'fee'
  | 'additionalSalaryInfo'
  | 'jobLocationType'
  | 'officeAttendanceFrequency'
  | 'jobLocations'
  | 'features'
  | 'jobRequest'
  | 'jobFeature1'
  | 'jobFeature2'
  | 'requiredSkills'
  | 'additionalSkillsInfo'
  | 'teamMembers'
  | 'teamGoals'
  | 'teamStrengths'
  | 'teamImage1'
  | 'teamImage2'
  | 'organizationAffiliation'
  | 'assignmentType'
  | 'businessContent'
  | 'businessImage1'
  | 'businessImage2'
  | 'businessChallenges'
  | 'designApproach'
  | 'designersCount'
  | 'evaluationMethod'
  | 'careerPath'
  | 'designerVoices'
  | 'pitch'
  | 'relatedLink1'
  | 'relatedLink2'
  | 'relatedLink3'
  | 'workingDays'
  | 'workingHours'
  | 'benefits'
  | 'holiday'
  | 'trialPeriod'
  | 'recruitmentSteps'
  | 'contractPeriod'

export const jobV2ModifyRequestIssueTargetOrderOnPreview: JobV2ModifyRequestIssueTarget[] =
  [
    'coverImage',
    'jobTitle',
    'features',
    'jobRequest',
    'jobFeature1',
    'jobFeature2',
    'requiredSkills',
    'additionalSkillsInfo',
    'teamMembers',
    'teamGoals',
    'teamStrengths',
    'teamImage1',
    'teamImage2',
    'organizationAffiliation',
    'assignmentType',
    'businessContent',
    'businessImage1',
    'businessImage2',
    'businessChallenges',
    'designApproach',
    'designersCount',
    'evaluationMethod',
    'careerPath',
    'designerVoices',
    'pitch',
    'relatedLink1',
    'relatedLink2',
    'relatedLink3',
    'salary',
    'additionalSalaryInfo',
    'fee',
    'displayOccupation',
    'employment',
    'contractPeriod',
    'workingDays',
    'jobLocations',
    'jobLocationType',
    'officeAttendanceFrequency',
    'workingHours',
    'benefits',
    'holiday',
    'trialPeriod',
    'recruitmentSteps',
  ]

export const jobV2ModifyRequestIssueTargetOrderOnEdit: JobV2ModifyRequestIssueTarget[] =
  [
    'jobTitle',
    'coverImage',
    'displayOccupation',
    'employment',
    'contractPeriod',
    'salary',
    'additionalSalaryInfo',
    'fee',
    'workingDays',
    'jobRequest',
    'jobFeature1',
    'jobFeature2',
    'requiredSkills',
    'additionalSkillsInfo',
    'teamMembers',
    'teamGoals',
    'teamStrengths',
    'teamImage1',
    'teamImage2',
    'organizationAffiliation',
    'assignmentType',
    'businessContent',
    'businessImage1',
    'businessImage2',
    'businessChallenges',
    'designApproach',
    'designersCount',
    'evaluationMethod',
    'careerPath',
    'designerVoices',
    'jobLocationType',
    'officeAttendanceFrequency',
    'jobLocations',
    'workingHours',
    'benefits',
    'holiday',
    'trialPeriod',
    'recruitmentSteps',
    'pitch',
    'relatedLink1',
    'relatedLink2',
    'relatedLink3',
    'features',
  ]

export const jobV2ModifyRequestIssueTargetLabels: {
  [K in JobV2ModifyRequestIssueTarget]: string
} = {
  coverImage: 'カバー画像',
  displayOccupation: '職種',
  jobTitle: '求人タイトル',
  employment: '雇用形態',
  salary: '年収',
  additionalSalaryInfo: '年収の補足',
  fee: '報酬',
  jobLocationType: '勤務場所',
  officeAttendanceFrequency: '出社頻度',
  jobLocations: '勤務地',
  features: '特徴',
  jobRequest: 'お願いしたいこと',
  jobFeature1: '特徴・訴求ポイント1',
  jobFeature2: '特徴・訴求ポイント2',
  requiredSkills: '求めるスキル',
  additionalSkillsInfo: '求めるスキルの補足',
  teamMembers: '所属メンバー',
  teamGoals: '目指していること・大切にしていること',
  teamStrengths: '強み・特徴',
  teamImage1: 'チームの画像1',
  teamImage2: 'チームの画像2',
  organizationAffiliation: '所属組織',
  assignmentType: '関わり方',
  businessContent: '事業内容',
  businessImage1: '事業の画像1',
  businessImage2: '事業の画像2',
  businessChallenges: '現在の課題・今後の展望',
  designApproach: '会社におけるデザインへの考え方',
  designersCount: 'デザイナーの在籍者数',
  evaluationMethod: 'デザイナー評価方法',
  careerPath: 'キャリアパス',
  designerVoices: '今いるデザイナーの声',
  pitch: '会社紹介資料',
  relatedLink1: '関連リンク1',
  relatedLink2: '関連リンク2',
  relatedLink3: '関連リンク3',
  workingDays: '稼働日数',
  workingHours: '勤務時間 / 形態',
  contractPeriod: '契約期間',
  benefits: '待遇 / 福利厚生',
  holiday: '休日 / 休暇',
  trialPeriod: '試用期間',
  recruitmentSteps: '採用ステップ',
}

export type JobV2ModifyRequestIssueTargetSummary = {
  target: JobV2ModifyRequestIssueTarget
  targetLabel: string
  issueCount: number
}

/**
 * 変更点確認: ターゲットごとの変更状態
 */
export type JobV2ChangeStatus = 'added' | 'removed' | 'changed' | 'unchanged'

export type JobV2TargetChange = {
  status: JobV2ChangeStatus
  /** テキスト項目で変更があった場合のみ、文中差分の元となるテキスト */
  beforeText?: string
  afterText?: string
}

export type JobV2Changes = Record<
  JobV2ModifyRequestIssueTarget,
  JobV2TargetChange
>

/**
 * 変更点確認: 比較の基準(変更前)となる求人の種類
 * - published: 公開中の求人(変更申請の申請確認)
 * - snapshot: 修正依頼送信時点のスナップショット(修正依頼への対応後の再申請の申請確認)
 */
export type JobV2ChangeCompareBaseType = 'published' | 'snapshot'

export type JobV2ChangeCompareBase = {
  type: JobV2ChangeCompareBaseType
  job: GetJobV2200ResponseJob
  /** type が snapshot のとき: 修正依頼の送信日時 */
  snapshotAt?: string
  /** type が snapshot のとき: 修正依頼の送信者名 */
  senderEmployeeName?: string
  /** type が snapshot のとき: 修正依頼の送信者画像パス */
  senderProfileImagePath?: string
}

/**
 * 変更点確認: 比較を開始するボタンの文言(基準の種類ごと)
 */
export const jobV2ChangeCompareBaseLabels: Record<
  JobV2ChangeCompareBaseType,
  string
> = {
  published: '公開版と比較',
  snapshot: '修正依頼時と比較',
}

/**
 * 変更点確認: provide/inject で共有する状態
 */
export type JobV2ChangeCompareState = {
  enabled: Ref<boolean>
  viewMode: Ref<JobV2ChangeCompareViewMode>
  /** 比較できる基準の一覧(公開中・修正依頼時のうち取得できたもの) */
  bases: Ref<JobV2ChangeCompareBase[]>
  /** 現在選択中の基準の種類 */
  baseType: Ref<JobV2ChangeCompareBaseType | undefined>
  changes: Ref<JobV2Changes | undefined>
}

/**
 * 変更点確認: 比較ビューの表示パターン
 */
export type JobV2ChangeCompareViewMode = 'split' | 'before' | 'after'

export const jobV2ChangeCompareViewModeItems: {
  label: string
  value: JobV2ChangeCompareViewMode
}[] = [
  { label: '比較', value: 'split' },
  { label: '変更前', value: 'before' },
  { label: '変更後', value: 'after' },
]

/**
 * 変更点確認: 比較ビューのどちら側か
 */
export type JobV2ChangeCompareSide = 'before' | 'after'

export const jobV2ChangeCompareSideLabels: Record<
  JobV2ChangeCompareBaseType,
  Record<JobV2ChangeCompareSide, string>
> = {
  published: {
    before: '変更前（公開中）',
    after: '変更後（申請内容）',
  },
  snapshot: {
    before: '変更前（修正依頼時）',
    after: '変更後（申請内容）',
  },
}

export const jobV2ChangeStatusLabels: Record<
  Exclude<JobV2ChangeStatus, 'unchanged'>,
  string
> = {
  added: '追加',
  removed: '削除',
  changed: '変更',
}

export const jobV2CanEdit: {
  [K in JobV2RequestStatus]: boolean
} = {
  [jobV2RequestStatus.NONE]: true,
  [jobV2RequestStatus.UNDER_REVIEW_PUBLISH]: false,
  [jobV2RequestStatus.NEED_TO_FIX_PUBLISH]: true,
  [jobV2RequestStatus.UNDER_REVIEW_PUBLISH_AFTER_FIX]: false,
  [jobV2RequestStatus.UNDER_REVIEW_UPDATE]: false,
  [jobV2RequestStatus.NEED_TO_FIX_UPDATE]: true,
  [jobV2RequestStatus.UNDER_REVIEW_UPDATE_AFTER_FIX]: false,
}

export const jobV2JobStatus = {
  ALL: 0,
  DRAFT: 1,
  UNDER_REVIEW: 2,
  NEED_TO_FIX: 3,
  PUBLISHED: 4,
  STOP: 5,
  APPROVED1: 11,
  APPROVED2: 12,
  CAN_PUBLISH: 13,
}

export const jobV2SortTypes = {
  DEFAULT: 0,
  JOB_UPDATED: 1,
  JOB_CREATED: 2,
  JOB_PUBLISHED: 3,
  COMPANY_NAME: 4,
  JOB_PUBLICATION_REQUESTED: 5,
  JOB_PUBLICATION_REQUESTED_MODIFY: 6,
} as const
export type JobV2SortTypes =
  (typeof jobV2SortTypes)[keyof typeof jobV2SortTypes]

export type ApprovalType = 'publishCheck1' | 'publishCheck2' | 'updateCheck'
export const approvalCheckItems = {
  11: '契約社員：契約期間が具体的に記載',
  12: '契約社員：契約更新あり→判断基準が明確に記載',
  13: '契約社員：更新上限あり→通算契約期間・更新回数上限の記載',
  21: '固定残業制：固定残業時間・割増賃金率の記載',
  31: '基本給：法定最低賃金以上',
  41: '年間休日数：法定基準を満たす',
  51: '各種手当：名称・金額が明確に記載',
  61: '関連リンク：外部求人リンクが無い',
  201: '誤字脱字',
  301: '変更箇所の確認',
} as const

export const jobV2ApprovalChecks: {
  [key in ApprovalType]: {
    id: keyof typeof approvalCheckItems
    label: string
  }[]
} = {
  publishCheck1: ([11, 51, 21, 61, 201] as const).map((id) => ({
    id,
    label: approvalCheckItems[id],
  })),
  publishCheck2: ([11, 12, 13, 31, 51, 21, 41, 61] as const).map((id) => ({
    id,
    label: approvalCheckItems[id],
  })),
  updateCheck: ([301] as const).map((id) => ({
    id,
    label: approvalCheckItems[id],
  })),
}

export const modifyRequestMessageTemplateOptions = [
  {
    label: '求人作成',
    value: `求人作成ありがとうございます。
内容を拝見しさらに良い求人にできるよう、
修正いただきたい部分がございます。
お手数おかけしますがご対応お願いします。`,
  },
  {
    label: '求人変更',
    value: `求人の変更にあたり、
修正いただきたい部分がございます。
お手数おかけしますがご対応お願いします。`,
  },
  {
    label: '再修正依頼',
    value: `求人の修正ありがとうございます。
内容を確認し、修正いただきたい部分がございます。
お手数おかけしますがご対応お願いします。`,
  },
]

export type JobV2EditFromValidationType = 'saveDraft' | 'requestCheck'

export const jobInterestStatusTypes = {
  NOT_INTERESTED: 0,
  INTERESTED: 1,
}

// 運営求人一覧のサービスフィルタ(service クエリ。API には services として送信)
export const jobV2ServiceTypes = {
  AGENT: 1,
  DIRECT: 2,
} as const
